import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import Button from '@mui/material/Button';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ImageIcon from '@mui/icons-material/Image';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import './RemoveBackgroundModal.scss';

export interface RemoveBackgroundModalProps {
  open: boolean;
  file: File | null;
  isProcessing: boolean;
  isUploading: boolean;
  processingMessage?: string;
  processingProgress?: number;
  errorMessage?: string | null;
  onRemoveBackground: () => void;
  onKeepBackground: () => void;
  onCancel: () => void;
  onRetry: () => void;
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const RemoveBackgroundModal: React.FC<RemoveBackgroundModalProps> = ({
  open,
  file,
  isProcessing,
  isUploading,
  processingMessage,
  processingProgress,
  errorMessage,
  onRemoveBackground,
  onKeepBackground,
  onCancel,
  onRetry,
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [file]);

  const isBusy = isProcessing || isUploading;

  const handleClose = (
    _event: object,
    reason: 'backdropClick' | 'escapeKeyDown'
  ) => {
    // Prevent accidental popup closure while background removal or upload is in progress
    if (isBusy) {
      return;
    }
    // Respect Escape and backdrop click when idle or on error
    if (reason === 'escapeKeyDown' || reason === 'backdropClick') {
      onCancel();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="remove-bg-dialog-title"
      aria-describedby="remove-bg-dialog-description"
      className="bg_removal_dialog"
      PaperProps={{
        className: 'bg_removal_dialog__paper',
      }}
    >
      <DialogContent className="bg_removal_dialog__content">
        <div className="bg_removal_dialog__icon_badge">
          <AutoFixHighIcon />
        </div>

        <h2 id="remove-bg-dialog-title" className="bg_removal_dialog__title">
          Remove Image Background?
        </h2>

        <p id="remove-bg-dialog-description" className="bg_removal_dialog__description">
          Choose whether you want to keep the original background or remove it before adding your image to the design.
        </p>

        {file && (
          <div className="bg_removal_dialog__preview_card">
            {previewUrl && (
              <img
                src={previewUrl}
                alt={file.name}
                className="bg_removal_dialog__thumb"
              />
            )}
            <div className="bg_removal_dialog__file_meta">
              <span className="bg_removal_dialog__file_name" title={file.name}>
                {file.name}
              </span>
              <span className="bg_removal_dialog__file_size">
                {formatFileSize(file.size)}
              </span>
            </div>
          </div>
        )}

        {isBusy && (
          <div className="bg_removal_dialog__processing_box">
            <CircularProgress size={28} className="bg_removal_dialog__processing_spinner" />
            <span className="bg_removal_dialog__processing_text">
              {processingMessage || (isUploading ? 'Uploading image...' : 'Processing image...')}
            </span>
            {typeof processingProgress === 'number' && processingProgress > 0 ? (
              <LinearProgress
                variant="determinate"
                value={Math.min(100, Math.max(0, processingProgress))}
                className="bg_removal_dialog__linear_progress"
              />
            ) : (
              <LinearProgress
                variant="indeterminate"
                className="bg_removal_dialog__linear_progress"
              />
            )}
          </div>
        )}

        {errorMessage && !isBusy && (
          <div className="bg_removal_dialog__error_box">
            <div className="bg_removal_dialog__error_title">
              <ErrorOutlineIcon />
              <span>Background Removal Failed</span>
            </div>
            <p className="bg_removal_dialog__error_message">{errorMessage}</p>
          </div>
        )}

        <div className="bg_removal_dialog__actions">
          {errorMessage && !isBusy ? (
            <div className="bg_removal_dialog__action_row">
              <Button
                className="bg_removal_dialog__btn_retry"
                onClick={onRetry}
                autoFocus
              >
                <RefreshIcon />
                Retry Removal
              </Button>
              <Button
                className="bg_removal_dialog__btn_keep"
                onClick={onKeepBackground}
              >
                <ImageIcon />
                Keep Background
              </Button>
            </div>
          ) : (
            <div className="bg_removal_dialog__action_row">
              <Button
                className="bg_removal_dialog__btn_remove"
                onClick={onRemoveBackground}
                disabled={isBusy}
                autoFocus
              >
                {isProcessing ? (
                  <>
                    <CircularProgress size={16} color="inherit" />
                    Removing...
                  </>
                ) : (
                  <>
                    <AutoFixHighIcon />
                    Remove Background
                  </>
                )}
              </Button>

              <Button
                className="bg_removal_dialog__btn_keep"
                onClick={onKeepBackground}
                disabled={isBusy}
              >
                {isUploading ? (
                  <>
                    <CircularProgress size={16} color="inherit" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <ImageIcon />
                    Keep Background
                  </>
                )}
              </Button>
            </div>
          )}

          <Button
            className="bg_removal_dialog__btn_cancel"
            onClick={onCancel}
            disabled={isBusy}
          >
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RemoveBackgroundModal;
